import React, { useRef } from 'react';
import { Avatar, Box, IconButton } from '@mui/material';
import { CameraAlt as CameraAltIcon } from '@mui/icons-material';
import { mediaSrc } from '../../api/conversations';

/**
 * Avatar that prefers an uploaded profile photo (mediaId) and falls back to
 * an initial letter. When `onChange` is provided, clicking the avatar opens
 * a file picker so the operator can upload/replace the photo.
 *
 * `onChange` receives a File and is expected to upload it (returning a media_id)
 * and persist it on the conversation.
 */
export const ChatAvatar = ({
  mediaId,
  name,
  size = 44,
  onChange,
  sx,
  bgColor = 'grey.300',
  fgColor = '#121826',
  bgColorActive = 'rgba(65,133,241,0.15)',
  fgColorActive = '#4185F1',
  active = false,
  showEditOnHover = true,
}) => {
  const url = mediaSrc(mediaId);
  const inputRef = useRef(null);
  const initial = (name || '?').charAt(0).toUpperCase();
  const editable = typeof onChange === 'function';

  const handlePick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) onChange(file);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: size,
        height: size,
        flexShrink: 0,
        cursor: editable ? 'pointer' : 'default',
        '& .photo-edit': { opacity: 0, transition: 'opacity .15s' },
        '&:hover .photo-edit': showEditOnHover && editable ? { opacity: 1 } : {},
        ...sx,
      }}
      onClick={editable ? () => inputRef.current?.click() : undefined}
    >
      <Avatar
        src={url || undefined}
        imgProps={{ loading: 'lazy', decoding: 'async' }}
        sx={{
          width: size,
          height: size,
          bgcolor: active ? bgColorActive : bgColor,
          color: active ? fgColorActive : fgColor,
          fontWeight: 700,
          fontSize: size > 60 ? '1.5rem' : '1rem',
        }}
      >
        {!url && initial}
      </Avatar>
      {editable && (
        <>
          <IconButton
            size="small"
            className="photo-edit"
            sx={{
              position: 'absolute',
              right: -4,
              bottom: -4,
              width: 22,
              height: 22,
              bgcolor: '#fff',
              boxShadow: 1,
              '&:hover': { bgcolor: '#f6f8fb' },
            }}
            onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
          >
            <CameraAltIcon sx={{ fontSize: 13, color: '#121826' }} />
          </IconButton>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={handlePick}
          />
        </>
      )}
    </Box>
  );
};
